import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Per-city section. Defined at module scope (not inline inside ManageAreas)
 * so React doesn't tear down & rebuild the subtree on every parent render.
 */
function AreaSection({ city, list, search, onAdd, onEdit, onDelete }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E7DDCF] overflow-hidden">
      <div className="px-5 py-3 bg-[#F5EFE6] border-b border-[#E7DDCF] flex items-center justify-between">
        <div>
          <div className="font-heading text-base">{city}</div>
          <div className="text-xs text-[#4B5563]">{list.length} area{list.length === 1 ? "" : "s"}</div>
        </div>
        <Button size="sm" onClick={() => onAdd(city)} variant="outline" className="gap-1" data-testid={`add-area-${city.toLowerCase()}-button`}>
          <Plus size={12} /> Add
        </Button>
      </div>
      <div className="divide-y divide-[#F1E8DC]">
        {list.length === 0 ? (
          <div className="text-sm text-[#4B5563] py-6 text-center">No areas in {city}{search && " matching your search"}.</div>
        ) : list.map((l) => (
          <div key={l.id} className="group flex items-center justify-between px-5 py-3" data-testid={`area-row-${l.name.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-3">
              <MapPin size={14} className="text-[#1F4E3D]" />
              <div>
                <div className="text-sm font-medium text-[#111827]">{l.name}</div>
                <div className="text-xs text-[#4B5563]">{l.spa_count} spa{l.spa_count === 1 ? "" : "s"} · /spas-in-{l.name.toLowerCase().replace(/\s/g, '-')}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100">
              <button onClick={() => onEdit(l)} className="p-1.5 rounded-md hover:bg-[#F5EFE6]" data-testid={`area-edit-${l.name.toLowerCase().replace(/\s/g, '-')}`}><Pencil size={13}/></button>
              <button onClick={() => onDelete(l)} disabled={l.spa_count > 0} className={`p-1.5 rounded-md ${l.spa_count > 0 ? "text-[#4B5563]/40 cursor-not-allowed" : "text-[#B42318] hover:bg-red-50"}`} data-testid={`area-delete-${l.name.toLowerCase().replace(/\s/g, '-')}`}><Trash2 size={13}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ManageAreas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", city: "PCMC" });
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/localities");
      setItems(r.data.items);
    } catch (e) {
      toast.error("Failed to load areas");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search) return items;
    return items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));
  }, [items, search]);

  const pcmc = filtered.filter((i) => i.city === "PCMC");
  const pune = filtered.filter((i) => i.city === "Pune");

  const openCreate = (city = "PCMC") => { setEditing(null); setForm({ name: "", city }); setOpen(true); };
  const openEdit = (loc) => { setEditing(loc); setForm({ name: loc.name, city: loc.city }); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) {
        await api.put(`/admin/localities/${editing.id}`, form);
        toast.success("Area updated");
      } else {
        await api.post("/admin/localities", form);
        toast.success("Area created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const del = async (loc) => {
    if (loc.spa_count > 0) return toast.error(`Cannot delete: ${loc.spa_count} spa(s) still use this area`);
    if (!window.confirm(`Delete area "${loc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/localities/${loc.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  // Per-city section is hoisted as <AreaSection> at module scope.

  return (
    <div data-testid="admin-areas-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading text-2xl">Manage Areas</h2>
          <p className="text-sm text-[#4B5563] mt-1">{items.length} localities across Pune & PCMC</p>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Search areas..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 w-56" data-testid="areas-search-input" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openCreate("PCMC")} data-testid="admin-add-area-button" className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2">
                <Plus size={14} /> Add Area
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Area</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Area Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Pashan" data-testid="area-name-input" />
                </div>
                <div>
                  <Label className="text-xs">City</Label>
                  <div className="flex gap-2 mt-1.5">
                    {["PCMC", "Pune"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setForm({ ...form, city: c })}
                        data-testid={`area-city-${c.toLowerCase()}`}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${form.city === c ? "bg-[#1F4E3D] text-white" : "bg-[#F5EFE6] text-[#1F2937] hover:bg-[#EFE7D8]"}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} className="bg-[#111827] text-white" data-testid="area-save-button">
                  {editing ? "Save Changes" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#4B5563]">Loading...</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <AreaSection city="PCMC" list={pcmc} search={search} onAdd={openCreate} onEdit={openEdit} onDelete={del} />
          <AreaSection city="Pune" list={pune} search={search} onAdd={openCreate} onEdit={openEdit} onDelete={del} />
        </div>
      )}

      <div className="mt-8 rounded-xl bg-[#F5EFE6] border border-[#E7DDCF] p-4 text-xs text-[#4B5563]">
        <strong className="text-[#111827]">Tip:</strong> Each area gets a dedicated SEO page at <code className="px-1 py-0.5 bg-white rounded">/spas-in-&lt;name&gt;</code>. Renaming will update all spas in that area automatically.
      </div>
    </div>
  );
}
